import { Card, tokens } from '@fluentui/react-components';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import type { IField, IFieldsTab } from '@moko/utils';
import { useMobileOtherInfo } from './MobileOtherInfoContext';
import { CompletionCount } from './CompletionCount';
import { CompletionBadge } from './CompletionBadge';

interface NavigationViewProps {
  fields: IField[];
  /**
   * When true, render in desktop split-layout mode:
   * - hides the "Navigate Sections" back-arrow header
   * - highlights the currently selected tab card
   */
  isDesktop?: boolean;
}

const getStatusColor = (completedFields: number, totalFields: number) => {
  if (totalFields === 0) return 'transparent';
  return completedFields === totalFields
    ? tokens.colorPaletteGreenBorder1
    : tokens.colorPaletteYellowBorder1;
};

export const NavigationView = ({
  fields,
  isDesktop = false,
}: NavigationViewProps) => {
  const {
    tabs,
    currentTabIndex,
    selectTab,
    setShowNavigation,
    getRepeatingPanelInstances,
    isRepeatingPanel,
    getTabCompletion,
  } = useMobileOtherInfo();

  const handleTabClick = (index: number) => {
    void selectTab(index);
  };

  const getRepeatingPanelsForTab = (tab: IFieldsTab): IField[] =>
    fields.filter(
      (f) =>
        f.tabName === tab.tabName &&
        f.fieldType === 'Panel' &&
        (f.parentName == null || f.parentName === 'root') &&
        f.effect !== 'Hide' &&
        (isRepeatingPanel(f.name).manual || isRepeatingPanel(f.name).entity)
    );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        width: '100%',
      }}
    >
      {!isDesktop && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 16px',
            backgroundColor: tokens.colorBrandBackground,
            color: tokens.colorNeutralForegroundOnBrand,
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 15,
            cursor: 'pointer',
          }}
          onClick={() => setShowNavigation(false)}
        >
          <ArrowLeft24Regular />
          Navigate Sections
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          marginTop: isDesktop ? 0 : 12,
          width: '100%',
        }}
      >
        {tabs.map((tab, index) => {
          const repeatingPanels = getRepeatingPanelsForTab(tab);
          const { completedFields, totalFields } = getTabCompletion(tab);
          const isActive = isDesktop && index === currentTabIndex;

          return (
            <div
              key={tab.tabName}
              style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            >
              <Card
                size="small"
                onClick={() => handleTabClick(index)}
                style={{
                  cursor: 'pointer',
                  width: '100%',
                  borderLeft: `3px solid ${getStatusColor(
                    completedFields,
                    totalFields
                  )}`,
                  backgroundColor: isActive
                    ? tokens.colorBrandBackground2
                    : undefined,
                  outline: isActive
                    ? `1px solid ${tokens.colorBrandStroke1}`
                    : undefined,
                }}
              >
                <div
                  style={{
                    padding: '6px 8px',
                    fontWeight: 600,
                    fontSize: 15,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {tab.caption}
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    <CompletionCount
                      completedFields={completedFields}
                      totalFields={totalFields}
                    />
                    <CompletionBadge
                      completedFields={completedFields}
                      totalFields={totalFields}
                    />
                  </div>
                </div>
              </Card>

              {!isDesktop &&
                repeatingPanels.map((panel) => {
                  const instances = getRepeatingPanelInstances(panel.name);
                  return (
                    <div
                      key={panel.name}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                      }}
                    >
                      <Card
                        size="small"
                        onClick={() => handleTabClick(index)}
                        style={{ cursor: 'pointer', marginLeft: 16 }}
                      >
                        <div style={{ padding: '4px 8px', fontSize: 13 }}>
                          {panel.label}
                        </div>
                      </Card>
                      {instances.map((inst, i) => (
                        <Card
                          key={i}
                          size="small"
                          onClick={() => handleTabClick(index)}
                          style={{ cursor: 'pointer', marginLeft: 32 }}
                        >
                          <div style={{ padding: '4px 8px', fontSize: 12 }}>
                            {inst.label}
                          </div>
                        </Card>
                      ))}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
};
